// src/components/chat/tabs/MessagesTab.tsx
'use client';

import { Message } from '../Message';
import { useChatStore } from '@/store/useChatStore';
import { useRef, useEffect } from 'react';
import { Send, AlertCircle, RotateCcw } from 'lucide-react';

function TypingIndicator() {
    return (
        <div className="flex items-center space-x-1 p-2 bg-gray-100 rounded-lg w-16">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
    );
}

interface MessagesTabProps {
    input: string;
    setInput: (value: string) => void;
    handleSubmit: (e: React.FormEvent) => void;
    handleRetry: () => void;
}

export function MessagesTab({ input, setInput, handleSubmit, handleRetry }: MessagesTabProps) {
    const { messages, isLoading, error, retryCount } = useChatStore();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading, error]);

    return (
        <div className="flex flex-col h-full">
            {/* Messages Area */}
            <div className="flex-1 p-4 overflow-y-auto">
                <div className="space-y-4">
                    {messages.length === 0 ? (
                        <div className="h-full flex items-center justify-center py-12">
                            <div className="text-center text-gray-500">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-3xl">💬</span>
                                </div>
                                <p className="text-lg font-medium">All your conversations will be here</p>
                                <p className="text-sm mt-2">Start a conversation with us!</p>
                            </div>
                        </div>
                    ) : (
                        messages.map((message) => (
                            <Message key={message.id} message={message} />
                        ))
                    )}

                    {isLoading && (
                        <div className="flex items-start">
                            <div className="p-2">
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                    <div className="w-6 h-6 rounded-full bg-pink-100" />
                                </div>
                            </div>
                            <TypingIndicator />
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                            <div className="flex items-center">
                                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                                <div>
                                    <p className="text-sm font-medium text-red-800">{error}</p>
                                    <button
                                        onClick={handleRetry}
                                        className="mt-1 inline-flex items-center text-sm text-red-700 hover:text-red-900"
                                    >
                                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                        {retryCount > 0 ? `Try again (${retryCount})` : 'Try again'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="border-t bg-white p-4">
                <div className="relative flex items-center">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 border rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                        disabled={isLoading}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit(e);
                            }
                        }}
                    />
                    <button
                        type="submit"
                        disabled={!input.trim() || isLoading}
                        className="absolute right-2 p-1.5 text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Send message"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Send size={20} />
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
